/**
 * This file is specifically for making simple Ajax calls and cache individual objects from response collection in localStorage.
 *
 * Parameters for makeHttpRequest method:
 *   {
 *       url: String,
 *       headers: Object (optional),
 *       storageKey: String (optional)
 *       callbackFunction: function reference to be executed after successful response with API result.
 *       expireTime: Number (optional) - cache expiration time in milliseconds
 *   }
 *
 * Usage:
 *     import makeHttpRequest from '../helpers/ajax-cache-collection-response';
 *     var request = {url: ___, storageKey: ___}
 *     makeHttpRequest(request).then(function(response) {
 *           ......
 *      }, function(error){
 *          .......
 *      });
 */
import appStorage from './app-storage';

const makeHttpRequest = async (options) => {
    const expiration = options.expireTime ? options.expireTime : 60 * 60 * 1000;

    const fetchOptions = {
        method: 'GET',
        headers: options.headers || {},
    };

    try {
        const response = await fetch(options.url, fetchOptions);

        if (!response.ok) {
            const failureData = { status: response.status, statusText: response.statusText };
            throw new Error(JSON.stringify(failureData));
        }

        const jsonData = await response.json();

        if (!jsonData.success) {
            throw new Error('API returned success: false');
        }

        const result = jsonData.result;

        // Cache individual items from the collection
        if (options.storageKey && result) {
            for (let i = 0; i < result.length; i++) {
                const storageKey = `${options.storageKey}_${result[i].lenderId}`;
                appStorage.set(storageKey, result[i], expiration);
            }
        }

        // Execute callback if provided
        if (options.callbackFunction) {
            options.callbackFunction(result);
        }

        return result;
    } catch (error) {
        // Re-throw if it's already our formatted error
        if (error.message.startsWith('{')) {
            throw error.message;
        }
        throw new Error(`Request failed: ${error.message}`, { cause: error });
    }
};

export default makeHttpRequest;
